import React, { useEffect, useRef, useState } from "react";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { UserPlus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useNotifications } from "../../context/NotificationContext";
import { UnreadDot } from "./UnreadDot";

/**
 * #5 Business Connection Invitation panel — shown on the Businesses page for
 * the receiver side. Lists incoming pending requests with Accept / Reject.
 * On accept, a supplier record is created on BOTH sides.
 *
 * Phase-1 (#1) — refetches automatically whenever the shared notification
 * poll's "businesses" unread count changes, so a brand-new incoming request
 * appears here within 5s with no manual refresh, driven by the SAME
 * underlying notification state as the popup toast.
 */
export function ConnectionRequestsPanel({ onChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { counts, list, markRead } = useNotifications();
  const bizCount = counts.by_section.businesses;
  const prevBizCount = useRef(bizCount);

  const load = () => {
    setLoading(true);
    apiClient.get("/connection-requests")
      .then((r) => setRequests(r.data || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  useEffect(() => {
    if (bizCount !== prevBizCount.current) {
      prevBizCount.current = bizCount;
      load();
    }
  }, [bizCount]);

  const respond = async (id, accept) => {
    setBusyId(id);
    try {
      await apiClient.post(`/connection-requests/${id}/${accept ? "accept" : "reject"}`);
      toast.success(accept ? "Connected — both businesses can now transact" : "Request declined");
      setRequests((rs) => rs.filter((r) => r.id !== id));
      // Accept/Reject IS the explicit "opened and acted on" moment for this
      // specific request — only now does its own notification clear, never
      // just from the panel being visible on the page.
      const n = list.find((x) => x.type === "connection_request_new" && x.entity_id === id && !x.read);
      if (n) markRead(n.id);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <div data-testid="connection-requests-panel" className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-3xl p-5 border border-amber-200 shadow-sm space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-2xl bg-amber-500 text-white grid place-items-center shadow-sm">
          <UserPlus className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-900 flex items-center gap-1.5">
            Connection requests
            <span data-testid="conn-req-count-badge" className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold grid place-items-center">
              {requests.length}
            </span>
          </div>
          <div className="text-xs text-slate-600">{requests.length} pending — Accept to link both businesses</div>
        </div>
      </div>
      <div className="space-y-2">
        {requests.map((req) => (
          <div
            key={req.id}
            data-testid={`conn-req-${req.id}`}
            className="bg-white border border-amber-100 rounded-2xl p-3 flex items-center gap-3 shadow-sm"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 grid place-items-center shrink-0 font-bold">
              {(req.from_name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate text-sm flex items-center gap-1.5">
                <UnreadDot entityId={req.id} />
                {req.from_name}
              </div>
              {req.from_phone && <div className="text-[11px] text-slate-500 truncate">{req.from_phone}</div>}
              {req.message && (
                <div className="text-[11px] text-slate-500 italic mt-0.5 truncate">&ldquo;{req.message}&rdquo;</div>
              )}
            </div>
            <button
              onClick={() => respond(req.id, false)}
              disabled={busyId === req.id}
              data-testid={`conn-req-reject-${req.id}`}
              className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 grid place-items-center transition disabled:opacity-40"
              aria-label="Reject"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={() => respond(req.id, true)}
              disabled={busyId === req.id}
              data-testid={`conn-req-accept-${req.id}`}
              className="h-9 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1 transition disabled:opacity-40"
            >
              <Check className="w-3.5 h-3.5" /> Accept
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
